import React, { useEffect, useState, useCallback } from "react";
import {
  Autosuggest,
  BreadcrumbGroup,
  Header,
  Input,
  Link,
  Multiselect,
  SpaceBetween,
  StatusIndicator,
  Table,
  Button,
  Box,
} from "@cloudscape-design/components";
import { ec2InstanceUrl } from "./awsConsoleUrls";
import { useNavigate } from "react-router-dom";
import { getAwsCreds, getDefaultRegion } from "./awsCredentials";
import { apiFetch } from "./apiFetch";
import { useDocumentTitle } from "./useDocumentTitle";

interface Account {
  documentId: string;
  Alias: string;
  accountid: string;
}

interface Instance {
  instanceId: string;
  name: string;
  instanceType: string;
  state: string;
  privateIp: string;
  publicIp: string;
  vpcId: string;
  subnetId: string;
  launchTime: string;
}

interface AccountState {
  instances: Instance[];
  loading: boolean;
  error: string | null;
  nextToken: string | null;
  tokenStack: string[];
}

const STATE_INDICATOR: Record<string, "success" | "stopped" | "error" | "warning"> = {
  running: "success",
  stopped: "stopped",
  terminated: "error",
  pending: "warning",
  stopping: "warning",
  "shutting-down": "warning",
};

const COLUMNS = [
  { id: "instanceId", header: "Instance ID", cell: (i: Instance) => i.instanceId, sortingField: "instanceId" },
  { id: "name", header: "Name", cell: (i: Instance) => i.name, sortingField: "name" },
  { id: "instanceType", header: "Type", cell: (i: Instance) => i.instanceType, sortingField: "instanceType" },
  {
    id: "state",
    header: "State",
    cell: (i: Instance) => (
      <StatusIndicator type={STATE_INDICATOR[i.state] || "info"}>{i.state}</StatusIndicator>
    ),
    sortingField: "state",
  },
  { id: "privateIp", header: "Private IP", cell: (i: Instance) => i.privateIp },
  { id: "publicIp", header: "Public IP", cell: (i: Instance) => i.publicIp },
  { id: "vpcId", header: "VPC", cell: (i: Instance) => i.vpcId, sortingField: "vpcId" },
  { id: "launchTime", header: "Launch Time", cell: (i: Instance) => i.launchTime, sortingField: "launchTime" },
];

export default function LiveEc2Page() {
  useDocumentTitle("EC2");
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<{ label: string; value: string }[]>([]);
  const [region, setRegion] = useState(getDefaultRegion());
  const [filters, setFilters] = useState({ instanceState: "", instanceType: "", vpcId: "", name: "" });
  const [accountStates, setAccountStates] = useState<Record<string, AccountState>>({});

  // Fetch accounts
  useEffect(() => {
    (async () => {
      const res = await apiFetch(`/accounts`);
      const { data } = await res.json();
      setAccounts(data);
      if (data.length > 0) {
        setSelectedOptions([{ label: `${data[0].Alias} (${data[0].accountid})`, value: data[0].documentId }]);
      }
    })();
  }, []);

  const selectedAccounts = accounts.filter((a) =>
    selectedOptions.some((o) => o.value === a.documentId)
  );

  const allInstances = selectedAccounts.flatMap((a) => accountStates[a.documentId]?.instances || []);

  const fetchInstances = useCallback(
    async (account: Account, nextToken?: string | null) => {
      const id = account.documentId;
      setAccountStates((prev) => ({
        ...prev,
        [id]: { ...(prev[id] || { instances: [], tokenStack: [], nextToken: null }), loading: true, error: null },
      }));

      const creds = getAwsCreds(account.Alias);
      if (!creds) {
        setAccountStates((prev) => ({
          ...prev,
          [id]: { instances: [], loading: false, error: "AWS credentials missing for this account.", nextToken: null, tokenStack: prev[id]?.tokenStack || [] },
        }));
        return;
      }

      const params = new URLSearchParams({ accountId: id, region, maxResults: "50" });
      if (nextToken) params.set("nextToken", nextToken);
      if (filters.instanceState) params.set("instanceState", filters.instanceState);
      if (filters.instanceType) params.set("instanceType", filters.instanceType);
      if (filters.vpcId) params.set("vpcId", filters.vpcId);
      if (filters.name) params.set("name", filters.name);

      try {
        const res = await apiFetch(`/inventory/live/ec2?${params}`, {
          headers: {
            "X-AWS-Access-Key-Id": creds.accessKeyId,
            "X-AWS-Secret-Access-Key": creds.secretAccessKey,
            "X-AWS-Session-Token": creds.sessionToken,
          },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setAccountStates((prev) => ({
          ...prev,
          [id]: { ...prev[id], instances: data.instances, nextToken: data.nextToken, loading: false, error: null },
        }));
      } catch (e: any) {
        setAccountStates((prev) => ({
          ...prev,
          [id]: { ...prev[id], instances: [], loading: false, error: e.message },
        }));
      }
    },
    [region, filters]
  );

  // Fetch when selected accounts or filters change
  useEffect(() => {
    selectedAccounts.forEach((account) => {
      setAccountStates((prev) => ({
        ...prev,
        [account.documentId]: { instances: [], loading: false, error: null, nextToken: null, tokenStack: [] },
      }));
      fetchInstances(account);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOptions, region, filters]);

  const handleNext = (account: Account) => {
    const st = accountStates[account.documentId];
    if (!st?.nextToken) return;
    setAccountStates((prev) => ({
      ...prev,
      [account.documentId]: { ...prev[account.documentId], tokenStack: [...(prev[account.documentId]?.tokenStack || []), ""] },
    }));
    fetchInstances(account, st.nextToken);
  };

  const handlePrevious = (account: Account) => {
    const st = accountStates[account.documentId];
    if (!st || st.tokenStack.length === 0) return;
    const newStack = [...st.tokenStack];
    newStack.pop();
    setAccountStates((prev) => ({
      ...prev,
      [account.documentId]: { ...prev[account.documentId], tokenStack: newStack },
    }));
    fetchInstances(account, newStack.length > 0 ? newStack[newStack.length - 1] : undefined);
  };

  return (
    <SpaceBetween size="l">
      <BreadcrumbGroup
        items={[
          { text: "Live Inventory", href: "/live-inventory" },
          { text: "EC2", href: "/live-inventory/ec2" },
        ]}
      />

      <div className="filter-bar">
        <Multiselect
          selectedOptions={selectedOptions}
          onChange={({ detail }) => setSelectedOptions(detail.selectedOptions as any)}
          options={accounts.map((a) => ({ label: `${a.Alias} (${a.accountid})`, value: a.documentId }))}
          placeholder="Select accounts"
          filteringType="auto"
        />
        <Input value={region} onChange={({ detail }) => setRegion(detail.value)} placeholder="Region" />
        <Autosuggest
          value={filters.instanceState}
          onChange={({ detail }) => setFilters((f) => ({ ...f, instanceState: detail.value }))}
          options={Array.from(new Set(allInstances.map((i) => i.state))).filter(Boolean).map((v) => ({ value: v }))}
          placeholder="State"
          enteredTextLabel={(v) => `Use: "${v}"`}
        />
        <Autosuggest
          value={filters.instanceType}
          onChange={({ detail }) => setFilters((f) => ({ ...f, instanceType: detail.value }))}
          options={Array.from(new Set(allInstances.map((i) => i.instanceType))).filter(Boolean).map((v) => ({ value: v }))}
          placeholder="Type"
          enteredTextLabel={(v) => `Use: "${v}"`}
        />
        <Autosuggest
          value={filters.vpcId}
          onChange={({ detail }) => setFilters((f) => ({ ...f, vpcId: detail.value }))}
          options={Array.from(new Set(allInstances.map((i) => i.vpcId))).filter(Boolean).map((v) => ({ value: v }))}
          placeholder="VPC ID"
          enteredTextLabel={(v) => `Use: "${v}"`}
        />
        <Autosuggest
          value={filters.name}
          onChange={({ detail }) => setFilters((f) => ({ ...f, name: detail.value }))}
          options={Array.from(new Set(allInstances.map((i) => i.name))).filter(Boolean).map((v) => ({ value: v }))}
          placeholder="Name"
          enteredTextLabel={(v) => `Use: "${v}"`}
        />
      </div>

      {selectedAccounts.map((account) => {
        const st = accountStates[account.documentId] || { instances: [], loading: false, error: null, nextToken: null, tokenStack: [] };

        if (st.error) {
          return (
            <Box key={account.documentId}>
              <Header>{account.Alias} ({account.accountid})</Header>
              <StatusIndicator type="error">{st.error}</StatusIndicator>
            </Box>
          );
        }

        return (
          <Table
            key={account.documentId}
            header={
              <Header
                actions={
                  <SpaceBetween size="xs" direction="horizontal">
                    <Button disabled={st.tokenStack.length === 0} onClick={() => handlePrevious(account)}>Previous</Button>
                    <Button disabled={!st.nextToken} onClick={() => handleNext(account)}>Next</Button>
                  </SpaceBetween>
                }
              >
                {account.Alias} ({account.accountid})
              </Header>
            }
            columnDefinitions={[
              ...COLUMNS,
              {
                id: "console",
                header: "AWS Console",
                cell: (i: Instance) =>
                  i.instanceId && region ? (
                    <span onClick={(e) => e.stopPropagation()}>
                      <Link
                        external
                        href={ec2InstanceUrl(i.instanceId, region)}
                        externalIconAriaLabel="Opens in AWS Console"
                      />
                    </span>
                  ) : (
                    "—"
                  ),
              },
            ]}
            items={st.instances}
            loading={st.loading}
            sortingDisabled={false}
            resizableColumns
            wrapLines
            stickyHeader
            onRowClick={({ detail }) => {
              const inst = detail.item as Instance;
              navigate(`/live-inventory/ec2/${inst.instanceId}?account=${account.documentId}&region=${region}`);
            }}
            empty="No instances found."
          />
        );
      })}
    </SpaceBetween>
  );
}
